import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, FadeIn } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { api, apiError, track } from "../lib/api";
import { setSeo } from "../lib/seo";

const EXPLORE = [
  { n: "01", t: "Responsibility", d: "How you feel about holding responsibility even when the work itself receives little recognition." },
  { n: "02", t: "Authority & Discretion", d: "How you navigate proximity to private lives, sensitive information and decision-making while maintaining professional boundaries." },
  { n: "03", t: "Emotional Awareness", d: "How you respond to tension, unspoken expectations and changing dynamics without absorbing what is not yours." },
  { n: "04", t: "Visibility & Value", d: "Whether meaningful work still feels meaningful when its success is often measured by what does not go wrong." },
];

const FOR_YOU = [
  "You're curious about Estate Management but don't fully understand the realities of the role",
  "You're considering moving from hospitality, personal assistance, property management or another service-focused profession",
  "You've recently discovered Estate Management as a possible career",
  "You're already working around private households and wondering whether this is the direction you want to pursue",
];

export default function GuideLanding() {
  useEffect(() => { setSeo({ title: "Is Estate Management Actually for Me? — Complimentary Guide · Marella Bolognesi", description: "A complimentary, reflective guide to help you understand the realities of Estate Management and consider whether the profession aligns with you." }); track("guide_landing_view"); }, []);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [guideUrl, setGuideUrl] = useState("");
  const scrollToForm = () => document.getElementById("capture")?.scrollIntoView({ behavior: "smooth", block: "start" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/guide/request", form);
      const url = `${BACKEND}/api/guide/download?token=${data.token}`;
      setGuideUrl(url); setDone(true);
      window.open(url, "_blank", "noopener");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="guide-landing-page" className="pt-20">
      {/* Hero */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-7">
          <FadeIn><MetaLabel>Complimentary Guide · Il Salotto Digitale</MetaLabel></FadeIn>
          <FadeIn delay={0.08}>
            <h1 className="mt-6 font-serif font-light text-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Is Estate Management<br />Actually for Me?
            </h1>
          </FadeIn>
          <FadeIn delay={0.16}>
            <p className="mt-8 font-serif text-2xl lg:text-3xl font-light text-ink-soft leading-snug">A reflective guide to understand fit — before commitment.</p>
          </FadeIn>
          <FadeIn delay={0.22}>
            <p className="mt-8 max-w-xl text-lg font-light text-ink-soft leading-relaxed">
              Estate Management can look appealing from the outside. But the realities of working inside a private household are more nuanced.
            </p>
            <p className="mt-5 max-w-xl text-lg font-light text-ink-soft leading-relaxed">
              This guide invites you to consider the parts of the profession that are discussed less often — responsibility, discretion, emotional awareness, boundaries and leadership that may happen largely behind the scenes.
            </p>
            <button onClick={scrollToForm} data-testid="hero-get-guide" className="group mt-10 inline-flex items-center gap-3 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors">
              Receive the guide <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </button>
          </FadeIn>
        </div>
        <FadeIn delay={0.2} className="lg:col-span-5">
          <div className="aspect-[3/4] overflow-hidden border border-line bg-surface">
            <img src="/course-preview/isem-cover.png" alt="Cover of the complimentary guide, Is Estate Management Actually for Me? by Marella Bolognesi" className="w-full h-full object-cover" />
          </div>
        </FadeIn>
      </section>

      {/* What you'll explore */}
      <section className="border-t border-line mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-28">
        <Reveal><MetaLabel className="mb-14">What You'll Explore</MetaLabel></Reveal>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
          {EXPLORE.map((b, i) => (
            <Reveal key={b.n} delay={(i % 2) * 0.08}>
              <span className="font-serif text-accent text-lg">{b.n}</span>
              <h3 className="mt-2 font-serif text-2xl lg:text-3xl text-ink">{b.t}</h3>
              <p className="mt-3 text-base font-light text-ink-soft leading-relaxed max-w-md">{b.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Not a test */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-12 py-24 lg:py-28 text-center">
          <Reveal><p className="text-[11px] uppercase tracking-ultra text-accent">Not a Test. A Point of Reflection.</p></Reveal>
          <Reveal delay={0.06}>
            <p className="mt-8 font-serif font-light text-2xl lg:text-3xl text-ink leading-snug max-w-3xl mx-auto">
              This guide does not give you a score, label your personality or tell you whether you should become an Estate Manager.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg font-light text-ink-soft leading-relaxed max-w-2xl mx-auto">
              Instead, it gives you space to notice where the realities of the profession feel natural, where they may require growth and where genuine resistance appears.
            </p>
            <p className="mt-8 text-sm uppercase tracking-[0.2em] text-ink-soft">The goal is not a verdict. It is clarity.</p>
          </Reveal>
        </div>
      </section>

      {/* Who it's for */}
      <section className="border-t border-line mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-20">
        <Reveal className="lg:col-span-4"><MetaLabel>This Guide May Be For You If…</MetaLabel></Reveal>
        <div className="lg:col-span-8 divide-y divide-line border-t border-line">
          {FOR_YOU.map((f, i) => (
            <Reveal key={i} delay={i * 0.05}><p className="py-6 text-lg font-light text-ink leading-relaxed">{f}</p></Reveal>
          ))}
        </div>
      </section>

      {/* Capture */}
      <section id="capture" className="border-t border-line bg-ink text-bg scroll-mt-24">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-28">
          {!done ? (
            <div data-testid="guide-capture" className="max-w-xl mx-auto text-center">
              <p className="text-[11px] uppercase tracking-ultra text-accent">Receive the Guide</p>
              <h2 className="mt-8 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">A complimentary volume from Il Salotto Digitale.</h2>
              <p className="mt-6 text-lg font-light text-bg/70 leading-relaxed">Add your name and email and the guide will open for you to read, with a copy sent to your inbox.</p>
              <form onSubmit={submit} className="mt-12 space-y-4 text-left">
                <input data-testid="guide-first-name" type="text" placeholder="First name" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="w-full bg-transparent border border-bg/30 px-5 py-4 text-bg placeholder:text-bg/40 focus:border-bg outline-none transition-colors" />
                <input data-testid="guide-email" type="email" placeholder="Email address" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border border-bg/30 px-5 py-4 text-bg placeholder:text-bg/40 focus:border-bg outline-none transition-colors" />
                <button type="submit" disabled={busy} data-testid="guide-submit"
                  className="group w-full inline-flex items-center justify-center gap-3 bg-bg text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-white transition-colors disabled:opacity-60">
                  {busy ? "Preparing your guide…" : "Read the complimentary guide"} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </button>
                <p className="pt-2 text-[11px] text-bg/45 text-center">No cost. Unsubscribe anytime. We respect your privacy.</p>
              </form>
            </div>
          ) : (
            <div data-testid="guide-access" className="max-w-xl mx-auto text-center">
              <p className="text-[11px] uppercase tracking-ultra text-accent">Your Guide Is Ready</p>
              <h2 className="mt-8 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">Thank you, {form.first_name}.</h2>
              <p className="mt-6 text-lg font-light text-bg/70 leading-relaxed">Your guide has opened in a new tab, and a copy is on its way to your inbox. You can reopen it below.</p>
              <a href={guideUrl} target="_blank" rel="noreferrer" data-testid="guide-read-link"
                className="group mt-10 inline-flex items-center gap-3 bg-bg text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-white transition-colors">
                Read the guide <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
